import { useLazyQuery, useQuery } from '@apollo/client/react';
import { PageSubTitle, PageTitle } from '@optools/components/layout/typography';
import { PageLoader } from '@optools/components/loading/page-loader';
import {
  GET_ACTIVE_LOCATION_OVERRIDES,
  GET_USER_INFO,
} from '@optools/services/operator-gateway/ip-override/queries';
import { ConvivaSessions } from '@optools/tools/ip-override/components/conviva-sessions';
import { ExistingOverridesTable } from '@optools/tools/ip-override/components/existing-overrides-table';
import { UserInfoSearch } from '@optools/tools/ip-override/components/user-info-search';
import { UserInfoTable } from '@optools/tools/ip-override/components/user-info-table';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo } from 'react';

export const Route = createFileRoute('/_auth/ip-override/user-lookup')({
  component: RouteComponent,
});

export function RouteComponent() {
  const [getUserInfo, { data, error, loading }] = useLazyQuery(GET_USER_INFO, {
    fetchPolicy: 'network-only',
  });

  const handleSubmit = (email: string) => {
    getUserInfo({ variables: { email } });
  };

  const { userInfo } = data || {};

  const locationOverrides = useQuery(GET_ACTIVE_LOCATION_OVERRIDES, {
    fetchPolicy: 'network-only',
    skip: !userInfo,
  });

  const userLocationOverrides = useMemo(
    () =>
      locationOverrides?.data?.getActiveLocationOverrides.overrides.filter(
        (item) => item.oktaId === userInfo?.id,
      ),
    [locationOverrides, userInfo],
  );

  return (
    <section className="max-w-7xl space-y-4">
      <div className="mb-6">
        <PageTitle>User Info Lookup</PageTitle>
        <PageSubTitle>
          Search for a user by email to view their information, existing overrides and recent
          Conviva sessions
        </PageSubTitle>
      </div>

      <div className="space-y-8">
        <UserInfoSearch loading={loading} onSubmit={handleSubmit} />

        {loading && <PageLoader />}

        {(error || (!error && userInfo === null)) && (
          <div>
            <h2 className="mb-1 text-lg font-bold">No Results</h2>
            <p className="text-sm">There is no user information for this email.</p>
          </div>
        )}

        {userInfo && !loading && (
          <div className="space-y-10">
            <UserInfoTable userInfo={userInfo} />

            <ExistingOverridesTable email={userInfo.email} overrides={userLocationOverrides} />

            <ConvivaSessions oktaId={userInfo.id} />
          </div>
        )}
      </div>
    </section>
  );
}
